import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'

import { editUser, getUser, getUserInfo } from 'app/actions/user'
import { getCompanies, getCompanyDetails } from 'app/actions/company'
import { CompanyType } from 'app/constants/companyType'
import { ROUTES } from 'app/constants/routes'
import { CompanyDetailsModel } from 'app/models/companyDetailsModel'
import { CompanyGroupModel, CompanyGroupRoleModel } from 'app/models/companyGroupRoleModel'
import { RolePermissionModel } from 'app/models/rolePermissionModel'
import { UserModel } from 'app/models/userModel'
import { validateEmail } from 'app/common/validationHelper'
import { isAdmin, saveUserInfo } from 'app/services/local/roleService'
import { getCompanyTypeIcon } from 'app/services/company/companyIconResolver'
import { ROLES_KEY, fetchRoles } from 'app/containers/administration/roleQueries'
import { GROUPS_KEY, fetchGroups } from 'app/containers/administration/groupQueries'

import { TableProvider } from '@/modules/drivers/context/TableContext'
import { AdminIcon, CheckboxField } from '@/modules/shared/components'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { Dropdown } from '@/app/features/company-selection/Dropdown'
import { toast } from '@/lib/toasts/api'
import { Button } from '@/ui/button'
import { TextField } from '@/ui/form/controls/TextField'
import { AdminColumn, AdminTable } from '../shared/AdminTable'
import { RolePicker } from './components/RolePicker'

/** `c_`/`g_` rozróżnia firmę od grupy - te same id mogą się powtórzyć. */
const objectId = (object: CompanyGroupModel) =>
  `${object.isGroup ? 'g' : 'c'}_${object.id}`

/** Role z firm i grup użytkownika, zwinięte do jednego wiersza na obiekt. */
const prepareRolesInfo = (
  user: UserModel,
  company: CompanyDetailsModel,
): CompanyGroupRoleModel[] => {
  if (user == null) return []

  const byCompany = new Map<string, CompanyGroupRoleModel>()
  for (const entry of user.roles ?? []) {
    if (!byCompany.has(entry.company.name)) {
      byCompany.set(entry.company.name, {
        object: { name: entry.company.name, id: entry.company.id },
        roles: entry.company.roles,
      })
    }
  }

  const byGroup = new Map<string, CompanyGroupRoleModel>()
  for (const entry of user.groups ?? []) {
    if (!byGroup.has(entry.group.name)) {
      byGroup.set(entry.group.name, {
        object: { name: entry.group.name, id: entry.group.id, isGroup: true },
        roles: entry.roles,
      })
    }
  }

  let rolesInfo = [...byCompany.values(), ...byGroup.values()]

  // Firma, z której wszedł admin, ma być na liście nawet bez ról - inaczej nie da
  // się jej przypisać pierwszej roli.
  if (company != null && company.type === CompanyType.NORMAL) {
    const alreadyThere = rolesInfo.some(
      (item) => !item.object.isGroup && String(item.object.id) === company.id,
    )
    if (!alreadyThere) {
      rolesInfo = [
        ...rolesInfo,
        { object: { id: company.id, name: company.companyName }, roles: [] },
      ]
    }
  }

  return rolesInfo
}

type Validation = {
  login: string | null
  firstName: string | null
  surname: string | null
  isValid: boolean
}

const validate = (user: UserModel, t: (key: string) => string): Validation => {
  const empty = (value?: string) => value == null || value.trim() === ''
  const login = empty(user?.login)
    ? t('administrationPanel-users.validation-login-empty')
    : !validateEmail(user.login)
      ? t('administrationPanel-users.validation-login-invalid')
      : null
  const firstName = empty(user?.firstName)
    ? t('administrationPanel-users.validation-firstName-empty')
    : null
  const surname = empty(user?.surname)
    ? t('administrationPanel-users.validation-surname-empty')
    : null

  return {
    login,
    firstName,
    surname,
    isValid: !login && !firstName && !surname,
  }
}

/**
 * Ekran edycji użytkownika panelu admina - funkcyjny odpowiednik
 * `app/components/administration/editUser.tsx` (klasa na `ValidatorBase`,
 * react-table v6 i bootstrapowym `Popover`).
 */
const AdminEditUserPageContent: React.FC = () => {
  const { t } = useLocalize()
  const navigate = useNavigate()
  const params = useParams<{ companyId?: string; userId: string }>()
  const companyId = params.companyId ?? null
  const userId = params.userId

  const [user, setUser] = React.useState<UserModel | null>(null)
  const [roles, setRoles] = React.useState<CompanyGroupRoleModel[]>([])
  const [flags, setFlags] = React.useState({
    globalAdmin: false,
    paymentAdmin: false,
    companyAdmin: false,
    demoCompanyAdmin: false,
  })
  const [canSave, setCanSave] = React.useState(false)
  const [submitted, setSubmitted] = React.useState(false)
  const [addOpen, setAddOpen] = React.useState<'company' | 'group' | null>(null)
  const [detailsOpen, setDetailsOpen] = React.useState(true)

  const { data: availableRoles = [] } = useQuery({
    queryKey: ROLES_KEY,
    queryFn: fetchRoles,
  })
  const { data: groups = [] } = useQuery({
    queryKey: GROUPS_KEY,
    queryFn: () => fetchGroups(companyId ?? undefined),
  })
  const { data: company = null } = useQuery({
    queryKey: ['admin', 'editUser', 'company', companyId],
    queryFn: () => getCompanyDetails(companyId, true),
    enabled: companyId != null,
  })
  const { data: companies = [] } = useQuery({
    queryKey: ['admin', 'editUser', 'companies', companyId],
    queryFn: () => getCompanies(companyId ?? undefined),
  })
  const userQuery = useQuery({
    queryKey: ['admin', 'editUser', userId, companyId],
    queryFn: () => getUser(userId, companyId),
  })

  React.useEffect(() => {
    const loaded = userQuery.data
    if (!loaded) return
    setUser(loaded)
    setFlags({
      globalAdmin: loaded.globalAdmin,
      paymentAdmin: loaded.paymentAdmin,
      companyAdmin: loaded.companyAdmin,
      demoCompanyAdmin: loaded.demoCompanyAdmin,
    })
    setRoles(prepareRolesInfo(loaded, company))
  }, [userQuery.data, company])

  const backUrl = companyId
    ? ROUTES.AdministrationPanel.CompanyTab.replace(
        ':companyId',
        companyId,
      ).replace(':tab', 'user')
    : ROUTES.AdministrationPanel.NoCompanyTab.replace(':tab', 'user')

  const validation = validate(user, t)
  const error = (message: string | null) => (submitted ? message : null)

  const patchUser = (patch: Partial<UserModel>) => {
    setUser((prev) => ({ ...prev, ...patch }))
    setCanSave(true)
  }

  const toggleRole = (row: CompanyGroupRoleModel, role: RolePermissionModel) => {
    setRoles((prev) =>
      prev.map((item) =>
        objectId(item.object) !== objectId(row.object)
          ? item
          : {
              ...item,
              roles: item.roles.some((existing) => existing.id === role.id)
                ? item.roles.filter((existing) => existing.id !== role.id)
                : [...item.roles, role],
            },
      ),
    )
    setCanSave(true)
  }

  // Firmy podrzędne wchodzą na listę razem z rodzicem; dla konta usługowego doklejamy
  // jeszcze samego tenanta, bo nie ma go we własnej liście firm.
  const selectableCompanies = React.useMemo(() => {
    const result: CompanyDetailsModel[] = []
    for (const item of companies) {
      result.push(item)
      for (const child of item.childCompanies ?? []) result.push(child)
    }
    if (
      company != null &&
      company.type === CompanyType.SERVICE &&
      result.length > 0 &&
      result[0].tenant != null
    ) {
      result.splice(0, 0, result[0].tenant as CompanyDetailsModel)
    }
    return result
  }, [companies, company])

  const companiesToAdd = selectableCompanies.filter(
    (item) => !roles.some((row) => objectId(row.object) === `c_${item.id}`),
  )
  const groupsToAdd = groups.filter(
    (item) => !roles.some((row) => objectId(row.object) === `g_${item.id}`),
  )

  const addRow = (row: CompanyGroupRoleModel, atTop: boolean) => {
    setRoles((prev) => (atTop ? [row, ...prev] : [...prev, row]))
    setAddOpen(null)
    setCanSave(true)
  }

  const save = async () => {
    setSubmitted(true)
    if (!validate(user, t).isValid) return

    const payload: UserModel = {
      ...user,
      globalAdmin: isAdmin() && flags.globalAdmin,
      paymentAdmin: isAdmin() && flags.paymentAdmin,
      companyAdmin: isAdmin() && flags.companyAdmin,
      demoCompanyAdmin: isAdmin() && flags.demoCompanyAdmin,
      companyId: user.companyId || companyId,
      companyRoles: roles
        .filter((row) => !row.object.isGroup)
        .map((row) => ({ companyId: row.object.id, roles: row.roles })),
      groupRoles: roles
        .filter((row) => row.object.isGroup)
        .map((row) => ({ groupId: row.object.id, roles: row.roles })),
    } as UserModel

    const saved = await editUser(payload)

    // Bez uprawnień admina lista firm i ról decyduje o tym, co widać lokalnie.
    if (!isAdmin()) {
      saveUserInfo(await getUserInfo(), await fetchRoles(), await getCompanies())
    }

    const details = `${user.firstName} ${user.surname}`
    if (saved.error == null) {
      toast.success({
        title: t('administrationPanel-users.user-edited'),
        text: details,
      })
      setCanSave(false)
      setRoles(prepareRolesInfo(saved, company))
    } else {
      toast.error({
        title: t('administrationPanel-users.user-not-edited'),
        text: details,
      })
    }
  }

  const columns: Array<AdminColumn<CompanyGroupRoleModel>> = [
    {
      key: 'type',
      header: t('administrationPanel-users.type'),
      width: 200,
      render: (row) => (
        <span className="tw-flex tw-items-center tw-gap-2">
          <AdminIcon name={row.object.isGroup ? 'company-group' : 'company'} />
          {row.object.isGroup
            ? t('administrationPanel-users.group')
            : t('administrationPanel-users.company')}
        </span>
      ),
    },
    {
      key: 'name',
      header: t('administrationPanel-users.name'),
      width: 400,
      render: (row) => row.object.name,
    },
    {
      key: 'role',
      header: t('administrationPanel-users.role'),
      render: (row) => (
        <RolePicker
          available={availableRoles as RolePermissionModel[]}
          companyId={row.object.isGroup ? undefined : String(row.object.id)}
          selected={row.roles}
          onToggle={(role) => toggleRole(row, role)}
        />
      ),
    },
    {
      key: 'delete',
      header: '',
      width: 58,
      align: 'center',
      render: (row) => (
        <AdminIcon
          name="delete"
          title={t('operation.delete')}
          onClick={() => {
            setRoles((prev) =>
              prev.map((item) =>
                objectId(item.object) === objectId(row.object)
                  ? { ...item, roles: [] }
                  : item,
              ),
            )
            setCanSave(true)
          }}
        />
      ),
    },
  ]

  if (!user) return null

  return (
    <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-bg-content-primary">
      <div className="tw-flex-shrink-0 tw-px-4 tw-pt-2">
        <Button
          variant="primary"
          mode="outline"
          size="sm"
          onClick={() => navigate(backUrl)}
        >
          <AdminIcon name="arrow-left" className="tw-h-4 tw-w-4" />
          {t('administrationPanel-companies.go-back')}
        </Button>
      </div>

      <div className="tw-flex-shrink-0 tw-border-b tw-border-base-300">
        <button
          type="button"
          className="tw-flex tw-w-full tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-left tw-text-sm tw-font-medium"
          onClick={() => setDetailsOpen((open) => !open)}
        >
          <AdminIcon name={detailsOpen ? 'arrow-up' : 'arrow-down'} />
          {t('administrationPanel-users.edit-user')}
        </button>

        {detailsOpen && (
          <div className="tw-grid tw-grid-cols-1 tw-gap-4 tw-p-4 md:tw-grid-cols-2 xl:tw-grid-cols-5">
            <TextField
              fullWidth
              label={t('administrationPanel-users.login')}
              value={user.login ?? ''}
              onChange={(event) => patchUser({ login: event.target.value })}
              error={error(validation.login)}
            />
            <TextField
              fullWidth
              label={t('administrationPanel-users.firstName')}
              value={user.firstName ?? ''}
              onChange={(event) => patchUser({ firstName: event.target.value })}
              error={error(validation.firstName)}
            />
            <TextField
              fullWidth
              label={t('administrationPanel-users.surname')}
              value={user.surname ?? ''}
              onChange={(event) => patchUser({ surname: event.target.value })}
              error={error(validation.surname)}
            />
            <TextField
              fullWidth
              label={t('administrationPanel-users.phone')}
              value={user.phone ?? ''}
              onChange={(event) => patchUser({ phone: event.target.value })}
            />
            {isAdmin() && (
              <div className="tw-flex tw-flex-col tw-gap-2">
                <CheckboxField
                  id="globalAdmin"
                  checked={flags.globalAdmin}
                  label={t('roles.admin')}
                  onChange={() => {
                    setFlags((prev) => ({ ...prev, globalAdmin: !prev.globalAdmin }))
                    setCanSave(true)
                  }}
                />
                <CheckboxField
                  id="paymentAdmin"
                  checked={flags.paymentAdmin}
                  label={t('roles.paymentAdmin')}
                  onChange={() => {
                    setFlags((prev) => ({ ...prev, paymentAdmin: !prev.paymentAdmin }))
                    setCanSave(true)
                  }}
                />
                <CheckboxField
                  id="companyAdmin"
                  checked={flags.companyAdmin}
                  label={t('roles.companyAdmin')}
                  onChange={() => {
                    setFlags((prev) => ({ ...prev, companyAdmin: !prev.companyAdmin }))
                    setCanSave(true)
                  }}
                />
                <CheckboxField
                  id="demoCompanyAdmin"
                  checked={flags.demoCompanyAdmin}
                  label={t('roles.demoCompanyAdmin')}
                  onChange={() => {
                    setFlags((prev) => ({
                      ...prev,
                      demoCompanyAdmin: !prev.demoCompanyAdmin,
                    }))
                    setCanSave(true)
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-6 tw-border-b tw-border-base-300 tw-px-4 tw-py-2">
        <span className="tw-text-sm tw-font-medium tw-uppercase">
          {t('administrationPanel-users.edit-companies')}
        </span>

        {companiesToAdd.length > 0 && (
          <div className="tw-relative">
            <Button
              variant="primary"
              mode="outline"
              size="sm"
              onClick={() => setAddOpen(addOpen === 'company' ? null : 'company')}
            >
              <AdminIcon name="add" className="tw-h-3 tw-w-3" />
              {t('administrationPanel-users.add-company')}
            </Button>
            {addOpen === 'company' && (
              <div className="drop-wrapper tw-absolute tw-left-0 tw-top-full tw-z-20 tw-mt-1 tw-w-[500px] tw-rounded-md tw-border tw-border-base-300 tw-bg-white tw-p-2 tw-shadow-lg">
                <Dropdown
                  searchPlaceholder={t('companySelection.find')}
                  placeholder={t('companySelection.company-name')}
                  options={companiesToAdd.map((company) => ({
                    value: company.id.toString(),
                    label: company.companyName,
                    content: (
                      <span className="tw-flex tw-items-center tw-gap-2">
                        <AdminIcon
                          name={getCompanyTypeIcon(
                            company.type,
                            company.accountingOnly,
                          )}
                          className="tw-h-4 tw-w-4"
                        />
                        {company.companyName}
                      </span>
                    ),
                  }))}
                  onChange={(value: string) => {
                    const found = companiesToAdd.find(
                      (item) => item.id.toString() === value,
                    )
                    if (!found) return
                    addRow(
                      {
                        object: { id: found.id, name: found.companyName },
                        roles: [],
                      },
                      true,
                    )
                  }}
                />
              </div>
            )}
          </div>
        )}

        {groupsToAdd.length > 0 && (
          <div className="tw-relative">
            <Button
              variant="primary"
              mode="outline"
              size="sm"
              onClick={() => setAddOpen(addOpen === 'group' ? null : 'group')}
            >
              <AdminIcon name="add" className="tw-h-3 tw-w-3" />
              {t('administrationPanel-users.add-group')}
            </Button>
            {addOpen === 'group' && (
              <div className="tw-absolute tw-left-0 tw-top-full tw-z-20 tw-mt-1 tw-w-[500px] tw-rounded-md tw-border tw-border-base-300 tw-bg-white tw-p-2 tw-shadow-lg">
                <Dropdown
                  compact
                  placeholder={t('administrationPanel-users.choose-group')}
                  options={groupsToAdd.map((item) => ({
                    value: item.id.toString(),
                    label: `${item.name}`,
                  }))}
                  onChange={(value) => {
                    const found = groups.find(
                      (item) => item.id.toString() === value,
                    )
                    if (!found) return
                    addRow(
                      {
                        object: {
                          id: Number(found.id),
                          name: found.name,
                          isGroup: true,
                        },
                        roles: [],
                      },
                      false,
                    )
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <AdminTable
        idPrefix="user-role"
        rows={roles}
        columns={columns}
        rowId={(row) => objectId(row.object)}
        selectable={false}
        isLoading={userQuery.isLoading}
      />

      {canSave && (
        <div className="tw-flex tw-flex-shrink-0 tw-justify-center tw-gap-2 tw-border-t tw-border-base-300 tw-py-2">
          <Button variant="primary" size="sm" onClick={save}>
            {t('operation.save')}
          </Button>
          <Button
            variant="primary"
            mode="outline"
            size="sm"
            onClick={() => navigate(backUrl)}
          >
            {t('operation.close')}
          </Button>
        </div>
      )}
    </div>
  )
}

export const AdminEditUserPage: React.FC = () => (
  <TableProvider>
    <AdminEditUserPageContent />
  </TableProvider>
)
